import React, { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";
import CtaSection from "../components/common/CtaSection";
import whygb from "../assets/logos/whygb.png";

const GOLD = "#50C878";
const NAVY = "#013220";

// ─── FREQUENTLY ASKED QUESTIONS ───────────────────────────────
// Buyer-facing FAQ content sourced from the GroupBuy Flats FAQ
// document. Linked from the footer only (not the main navbar).
// ─────────────────────────────────────────────────────────────

const FAQ_ITEMS = [
  {
    q: "Why should I join a GroupBuy?",
    a: "The primary advantage of GroupBuy is to secure better pricing through collective bargaining. When a large group of buyers come together, developers are more willing to offer attractive pricing and benefits.",
  },
  {
    q: "How do you achieve better pricing?",
    a: "We form a group of interested buyers and negotiate directly with the developer on behalf of the group. The larger the group, the stronger the negotiating power.",
  },
  {
    q: "Do you have a fixed price beforehand?",
    a: "No. Prices are not fixed in advance. Negotiations happen collectively, and we maintain a transparent process throughout.",
  },
  {
    q: "I have many questions. Can you help?",
    a: "Absolutely. We understand that purchasing real estate is a major decision. Our team is available to answer your questions and help you make an informed decision. Feel free to contact us anytime.",
  },
  {
    q: "Do investors pay any fee to join?",
    a: "Joining a GroupBuy is completely free. We charge a standard facilitation fee only when a successful purchase transaction is completed.",
  },
  {
    q: "Do you arrange site visits?",
    a: "No. Buyers are free to visit the developer's project site directly, inspect the property, and choose the unit that best suits their requirements.",
  },
  {
    q: "What if I purchase directly from the developer?",
    a: "You are free to do so. Our objective is to help buyers obtain a better group-negotiated price, which is often more advantageous than an individual purchase. If you prefer not to wait for a group negotiation, you may proceed independently.",
  },
  {
    q: "What if I don't have time to wait?",
    a: "There is no obligation to wait. You may proceed with your purchase independently. GroupBuy participation is entirely voluntary.",
  },
  {
    q: "Why do premium developers offer discounts?",
    a: "Developers often require funding at various stages of a project. When they see a large pool of committed buyers, they may choose to release inventory at special pricing. GroupBuy creates this opportunity for buyers to benefit from collective purchasing power.",
  },
  {
    q: "Are all projects RERA approved?",
    a: "Yes. We only onboard projects that have valid RERA approvals and comply with applicable regulations.",
  },
  {
    q: "How can investors trust GroupBuy?",
    a: "There is no need for blind trust. We do not collect any money from buyers before a transaction is completed. Our success is directly linked to a successful purchase by the buyer.",
  },
  {
    q: "What if someone asks me for money before the sale?",
    a: "Please do not make any payment. It is strictly against company policy. Report such incidents immediately. Any fees payable to GroupBuy must be paid only after the sale and only through official banking channels to the company.",
  },
  {
    q: "Can I visit your office?",
    a: "Yes. You are always welcome to visit our office and interact with our team.",
  },
];

function FaqRow({ item, isOpen, onToggle }) {
  return (
    <div
      style={{
        background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBF8 100%)",
        border: "1px solid rgba(80,200,120,0.18)",
        borderRadius: "12px",
        overflow: "hidden",
        transition: "border 0.2s",
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          padding: "20px 24px",
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "1.25rem",
            fontWeight: 600,
            color: NAVY,
            lineHeight: 1.3,
          }}
        >
          {item.q}
        </span>
        <ChevronDown
          size={22}
          color={GOLD}
          style={{
            flexShrink: 0,
            transition: "transform 0.25s ease",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>
      <div
        style={{
          maxHeight: isOpen ? "320px" : "0",
          overflow: "hidden",
          transition: "max-height 0.3s ease",
        }}
      >
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "0.98rem",
            color: "rgba(1,50,32,0.76)",
            lineHeight: 1.8,
            margin: 0,
            padding: "0 24px 22px",
          }}
        >
          {item.a}
        </p>
      </div>
    </div>
  );
}

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div style={{ background: "#F2F8F3", color: NAVY, minHeight: "100vh", paddingTop: "68px" }}>
      {/* Hero */}
      <div
        style={{
          position: "relative",
          backgroundImage: `url(${whygb})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          borderBottom: "1px solid rgba(80,200,120,0.2)",
          overflow: "hidden",
          height: "46vh",
          minHeight: "320px",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "flex-start",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg, rgba(15,30,50,0.85) 0%, rgba(15,30,50,0.6) 40%, rgba(15,30,50,0.2) 70%, rgba(15,30,50,0) 100%)",
            zIndex: 1,
          }}
        />
        <div style={{ position: "absolute", bottom: "48px", left: "60px", zIndex: 2, maxWidth: "650px", padding: "0 24px" }}>
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "0.6rem",
              color: GOLD,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              textShadow: "0 1px 4px rgba(0,0,0,0.45)",
              display: "block",
              marginBottom: "6px",
            }}
          >
            Have Questions?
          </span>
          <h1
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(2rem, 4vw, 3rem)",
              fontWeight: 600,
              margin: 0,
              color: "#FFFFFF",
              textShadow: "0 2px 14px rgba(0,0,0,0.5)",
              lineHeight: 1.2,
            }}
          >
            Frequently Asked Questions
          </h1>
        </div>
      </div>

      {/* FAQ accordion */}
      <section style={{ maxWidth: "860px", margin: "0 auto", padding: "60px 24px 70px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "28px" }}>
          <HelpCircle size={26} color={GOLD} />
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.8rem", fontWeight: 600, margin: 0 }}>
            Everything you need to know
          </h2>
        </div>
        <div style={{ display: "grid", gap: "14px" }}>
          {FAQ_ITEMS.map((item, i) => (
            <FaqRow
              key={item.q}
              item={item}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
            />
          ))}
        </div>
      </section>

      <CtaSection
        eyebrow="Still have questions?"
        title="Talk to our team directly"
        subtitle="Our advisors are happy to walk you through the GroupBuy process and answer anything else you'd like to know."
        primaryLabel="Contact Us"
        primaryTo="/contact"
      />
    </div>
  );
}
